<template>
    <OptionAbstract
        class="option-asset"
        :class="{ compact }"
        :selectable="isSelectable"
        :compact="compact">

        <!-- perspectives -->
        <div v-if="hasPerspectives" class="perspectives perspectives--desktop">
            <v-chip
                v-for="option in perspectives"
                :key="option"
                class="perspective-chip"
                :class="{ 'perspective-chip--active': isPerspectiveSelected(option) }"
                @click="handlePerspective(option)">
                {{ option }}
            </v-chip>
        </div>

        <!-- mobile perspectives -->
        <div v-if="hasPerspectives" class="perspectives perspectives--mobile">
            <v-select
                hide-details
                class="perspectives-select"
                variant="outlined"
                density="comfortable"
                item-title="title"
                item-value="value"
                :items="perspectiveOptions"
                :model-value="selectedPerspective"
                :placeholder="perspectivePlaceholder"
                @update:model-value="handlePerspective" />
        </div>

        <!-- asset preview -->
        <AssetItem 
            animation="dots"
            class="asset-item"
            :class="{ 'prefix': hasPerspectives }" 
            :asset="asset" 
            :animation="animation"
            :selectable="false" 
            :background="false" 
            :border="false" 
            :fileKey="fileKey" 
            @click="handleClick" 
            @delete="handleDelete" />

    </OptionAbstract>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import AssetItem from '@/components/common/asset/AssetItem.vue'
import OptionAbstract from '@/components/flow/options/OptionAbstract.vue'

export default {
    name: 'OptionAsset',
    components: {
        AssetItem,
        OptionAbstract,
    },
    props: {
        asset: {
            type: Object,
            required: true
        },
        fileKey: {
            type: String,
            default: null // if null, use thumbnail instead of file key
        },
        animation: {
            type: String,
            default: 'skeleton',
            validate(value) {
                return ['skeleton', 'progress', 'dots', 'none'].includes(value)
            }
        },
        selectable: {
            type: Boolean,
            default: true
        },
        menu: {
            type: Boolean,
            default: true
        },
        hasPerspectives: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            status: null
        }
    },
    computed: {
        keyFileStatusReady() {
            return ASSETS.FILES.STATUS.READY
        },

        isReady() {
            if (this.state === ASSETS.FILES.STATE.RETRIEVED) {
                return this.data !== null && this.data !== undefined && this.data !== ''
            }
            return false
        },
        isSelectable() {
            return this.selectable && this.isReady
        },

        perspectives() {
            return ['front', 'back', 'left', 'right', 'top', 'bottom', 'diagonal']
        },

        perspectiveOptions() {
            return this.perspectives.map(option => ({
                value: option,
                title: option.charAt(0).toUpperCase() + option.slice(1)
            }))
        },

        perspectivePlaceholder() {
            return this.selectedPerspective ? undefined : 'Select'
        },

        selectedPerspective() {
            return this.asset?.additional?.perspective || null
        },
        
        resource() {
            if (this.fileKey) return this.asset?.files[this.fileKey] || null
            return this.asset?.thumbnail || null
        },
        data() {
            return this.resource?.data || null
        },
        state() {
            return this.resource?.state || null
        },
    },
    emits: ['click', 'delete', 'perspective'],
    methods: {
        isPerspectiveSelected(perspective) {
            return this.selectedPerspective === perspective
        },
        handleClick() {
            if (!this.isSelectable) return
            this.$emit('click')
        },
        handleDelete() {
            this.$emit('delete')
        },
        handlePerspective(perspective) {
            if (!perspective || this.selectedPerspective === perspective) return
            this.$emit('perspective', {
                asset: this.asset,
                perspective
            })
        },
        handleStatus(status) {
            this.status = status
        }
    }
}
</script>

<style>
.option-asset .asset-item.prefix {
    border-top-left-radius: 0px !important;
    border-top-right-radius: 0px !important;
}

.option-asset .asset-item.prefix .image {
    border-top-left-radius: 0px !important;
    border-top-right-radius: 0px !important;
}

.option-asset .asset-item.prefix .image img {
    border-top-left-radius: 0px !important;
    border-top-right-radius: 0px !important;
}

.option-asset .perspectives-select .v-field__input {
    padding-top: 0px;
    padding-bottom: 0px;
    min-height: 32px;
    display: flex;
    flex-direction: row;
    align-items: center;
    align-content: center;
}

.option-asset .perspectives-select .v-field__input input {
    height: 100%;
}

.option-asset .asset-item .image img {
    width: 100%;
    height: 100%;
    border-top-left-radius: 0px !important;
    border-top-right-radius: 0px !important;
    object-fit: cover;
}
</style>

<style scoped>
.option-asset {
    box-sizing: content-box;
    aspect-ratio: unset;
    height: unset;
    border-radius: 10px !important;
}

.option-asset.compact.option-abstract-compact {
    height: unset;
    justify-content: flex-start;
}

.option-asset .perspectives {
    box-sizing: border-box;
    width: 100%;
    padding: 5px;
}

.option-asset .perspectives--desktop {
    padding-left: 0px;
    padding-right: 0px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 5px;
    background-color: rgba(var(--v-theme-darkbase), 1);
    border-bottom: 1px solid rgb(var(--v-theme-lightbase));
}

.option-asset .perspectives--mobile {
    display: none;
    flex-direction: column;
    align-items: stretch;
    gap: 5px;
}

.option-asset .perspectives-select {
    width: 100%;
}

.option-asset .perspectives .perspective-chip {
    font-size: 1.0rem;
    padding-left: 10px;
    padding-right: 10px;
    text-transform: capitalize;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.option-asset .perspectives .perspective-chip--active {
    background-color: rgb(var(--v-theme-color1));
    color: rgb(var(--v-theme-white));
    box-shadow: 0 0 0 1px rgba(var(--v-theme-color1), 0.4);
}

@media (max-width: 1800px) {
    .option-asset .perspectives .perspective-chip {
        font-size: 0.9rem;
    }
}

@media (max-width: 1000px) {
    .option-asset .perspectives {
        padding: 3px;
    }

    .option-asset .perspectives--desktop {
        gap: 3px;
    }

    .option-asset .perspectives .v-chip {
        font-size: 0.8rem;
    }
}

@media (max-width: 800px) {
    .option-asset .perspectives .v-chip {
        padding-left: 8px;
        padding-right: 8px;
    }
}

@media (max-width: 600px) {
    .option-asset .perspectives .v-chip {
        padding-left: 4px;
        padding-right: 4px;
        font-size: 0.7rem;
    }
}

@media (max-width: 700px) {
    .option-asset .perspectives--desktop {
        display: none;
    }

    .option-asset .perspectives--mobile {
        display: flex;
        padding: 3px;
    }
}

</style>
